import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { PencilFill, TrashFill } from "react-bootstrap-icons";

import { useSwipe } from "hoc/SwipeContext";

import "./styles.css";

const EDIT_ACTION_WIDTH = 80;   // revealed on right swipe
const DELETE_ACTION_WIDTH = 80;   // revealed on left swipe
const THRESHOLD = 50;   // px to commit to open
const FULL_SWIPE_THRESHOLD = 170;  // px — stopper & full-swipe trigger

export default function SwipeableItem({
	id,
	children,
	onEdit,
	onDelete,
	onClick,
	className,
	swipeDisabled
}) {
	const hasEdit = !!onEdit;
	const hasDelete = !!onDelete;
	const hasActions = (hasEdit || hasDelete) && !swipeDisabled;

	const x = useMotionValue(0);
	const isDragging = useRef(false);
	const ref = useRef(null);

	const dragStartPos = useRef({ x: 0, y: 0 });
	const isHorizontalDrag = useRef(false);

	const [lockScroll, setLockScroll] = useState(false);

	const { openId, setOpenId } = useSwipe();

	// Button always occupies FULL_SWIPE_THRESHOLD px. Icon translates so it stays
	// centered in the *revealed* portion — ACTION_WIDTH at rest, full width at full swipe.
	const editIconX = useTransform(
		x,
		[0, FULL_SWIPE_THRESHOLD],
		[-(FULL_SWIPE_THRESHOLD / 2), 0]
	);

	const deleteIconX = useTransform(
		x,
		[-FULL_SWIPE_THRESHOLD, 0],
		[0, FULL_SWIPE_THRESHOLD / 2]
	);

	const close = () =>
		animate(x, 0, { type: "spring", stiffness: 300, damping: 30 });

	const openEdit = () =>
		animate(x, EDIT_ACTION_WIDTH, { type: "spring", stiffness: 300, damping: 30 });

	const openDelete = () =>
		animate(x, -DELETE_ACTION_WIDTH, { type: "spring", stiffness: 300, damping: 30 });

	useEffect(() => {
		if (openId !== id) close();
	}, [openId]);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (ref.current && !ref.current.contains(e.target)) {
				close();
				setOpenId(null);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	const handleDragStart = (_event, info) => {
		if (!hasActions) return;
		dragStartPos.current = { x: info.point.x, y: info.point.y };
		isHorizontalDrag.current = false;
		isDragging.current = true;
	};

	const handleDrag = (_event, info) => {
		if (!hasActions) return;

		const deltaX = Math.abs(info.point.x - dragStartPos.current.x);
		const deltaY = Math.abs(info.point.y - dragStartPos.current.y);

		// Lock in direction once we have enough movement to be confident
		if (!isHorizontalDrag.current && (deltaX > 10 || deltaY > 10)) {
			isHorizontalDrag.current = deltaX > deltaY;
		}

		if (!isHorizontalDrag.current) {
			// Vertical scroll — kill horizontal motion immediately
			animate(x, 0, { duration: 0 });
		} else {
			// Confirmed horizontal — prevent scroll
			setLockScroll(true);
		}
	};

	const handleDragEnd = () => {
		if (!hasActions) return;

		setLockScroll(false);
		setTimeout(() => (isDragging.current = false), 50);

		// Vertical scroll gesture — reset and bail
		if (!isHorizontalDrag.current) {
			close();
			setOpenId(null);
			return;
		}

		const current = x.get();

		if (hasEdit && current >= FULL_SWIPE_THRESHOLD) {
			close(); setOpenId(null); onEdit?.(); return;
		}
		if (hasDelete && current <= -FULL_SWIPE_THRESHOLD) {
			close(); setOpenId(null); onDelete?.(); return;
		}
		if (hasEdit && current > THRESHOLD) {
			openEdit(); setOpenId(id); return;
		}
		if (hasDelete && current < -THRESHOLD) {
			openDelete(); setOpenId(id); return;
		}
		close();
		setOpenId(null);
	};

	return (
		<div
			ref={ref}
			className={className}
			style={{ touchAction: lockScroll ? "none" : "pan-y" }}
		>
			{/* ── EDIT action (left side, revealed on right swipe) ── */}
			{hasEdit && (
				<button
					className="btn swipeable-action-button position-absolute start-0 top-0 h-100"
					style={{
						width: FULL_SWIPE_THRESHOLD,
						backgroundColor: "#3685F7",
						padding: 0,
						zIndex: openId === id ? 1 : 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
					}}
					onClick={(e) => {
						e.stopPropagation();
						close(); setOpenId(null); onEdit?.();
					}}
				>
					<motion.span style={{ x: editIconX, display: "flex" }}>
						<PencilFill size={20} />
					</motion.span>
				</button>
			)}

			{/* ── DELETE action (right side, revealed on left swipe) ── */}
			{hasDelete && (
				<button
					className="btn swipeable-action-button position-absolute end-0 top-0 h-100"
					style={{
						width: FULL_SWIPE_THRESHOLD,
						backgroundColor: "#ed5545",
						padding: 0,
						zIndex: openId === id ? 1 : 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
					}}
					onClick={(e) => {
						e.stopPropagation();
						close(); setOpenId(null); onDelete?.();
					}}
				>
					<motion.span style={{ x: deleteIconX, display: "flex" }}>
						<TrashFill size={20} />
					</motion.span>
				</button>
			)}

			{/* ── CONTENT ── */}
			<motion.div
				drag={hasActions ? "x" : false}
				dragConstraints={
					hasActions
						? {
							left: hasDelete ? -FULL_SWIPE_THRESHOLD : 0,
							right: hasEdit ? FULL_SWIPE_THRESHOLD : 0,
						}
						: undefined
				}
				dragElastic={0}
				style={{ x: hasActions ? x : 0, zIndex: 2, position: "relative" }}
				onDragStart={handleDragStart}
				onDrag={handleDrag}
				onDragEnd={handleDragEnd}
				onClick={(e) => {
					if (isDragging.current) { e.stopPropagation(); return; }
					onClick?.(e);
				}}
			>
				{children}
			</motion.div>
		</div>
	);
}